import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useStore } from '@/store/useStore';
import { Trophy, ArrowLeft, Sparkles } from 'lucide-react';

export function WheelPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currentRaffle, fetchRaffleDetails } = useStore();
  const [spinning, setSpinning] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [winner, setWinner] = useState<number | null>(null);

  useEffect(() => {
    if (id) {
      fetchRaffleDetails(Number(id));
    }
  }, [id, fetchRaffleDetails]);

  const participants = currentRaffle?.participants || [];

  const spinWheel = () => {
    if (spinning || !currentRaffle) return;

    setSpinning(true);

    // Simulate random winner selection
    const randomIndex = Math.floor(Math.random() * participants.length);
    const winnerUserId = participants[randomIndex]?.user_id;

    // Calculate rotation (5 full spins + random angle to land on winner)
    const baseRotation = 360 * 5;
    const segmentAngle = 360 / participants.length;
    const targetAngle = randomIndex * segmentAngle;
    const finalRotation = baseRotation + (360 - targetAngle);

    setRotation(finalRotation);

    // Show winner after animation
    setTimeout(() => {
      setWinner(winnerUserId);
      setSpinning(false);
    }, 4000);
  };

  if (!currentRaffle) {
    return (
      <div className="min-h-screen bg-tg-theme-bg flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-tg-theme-text">Загрузка...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 text-white pb-20">
      {/* Header */}
      <div className="p-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center space-x-2 text-white/80 hover:text-white mb-4"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Назад</span>
        </button>

        <div className="text-center">
          <h1 className="text-3xl font-bold mb-2">{currentRaffle.title}</h1>
          <p className="text-purple-200">Розыгрыш призового фонда</p>
        </div>
      </div>

      {/* Prize Pool */}
      <div className="px-6 mb-8">
        <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 text-center">
          <Trophy className="w-12 h-12 text-yellow-400 mx-auto mb-3" />
          <p className="text-purple-200 mb-2">Призовой фонд</p>
          <p className="text-4xl font-bold text-yellow-400">
            {(currentRaffle.entry_price * participants.length * 0.9).toFixed(0)} ₽
          </p>
          <p className="text-sm text-purple-200 mt-2">
            Билетов: {participants.length}
          </p>
        </div>
      </div>

      {/* Wheel Container */}
      <div className="px-6 mb-8">
        <div className="relative aspect-square max-w-sm mx-auto">
          {/* Arrow Indicator */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-2 z-10">
            <div className="w-0 h-0 border-l-[20px] border-l-transparent border-r-[20px] border-r-transparent border-t-[30px] border-t-yellow-400 drop-shadow-lg"></div>
          </div>

          {/* Wheel */}
          <div
            className="w-full h-full rounded-full border-8 border-yellow-400 shadow-2xl overflow-hidden relative"
            style={{
              transform: `rotate(${rotation}deg)`,
              transition: spinning ? 'transform 4s cubic-bezier(0.17, 0.67, 0.12, 0.99)' : 'none',
            }}
          >
            {participants.map((participant, index) => {
              const angle = (360 / participants.length) * index;
              const color = index % 2 === 0 ? 'bg-purple-600' : 'bg-blue-600';

              return (
                <div
                  key={participant.user_id}
                  className={`absolute w-1/2 h-1/2 origin-bottom-right ${color}`}
                  style={{
                    transform: `rotate(${angle}deg) skewY(${-90 + 360 / participants.length}deg)`,
                    left: '50%',
                    top: '50%',
                  }}
                >
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-white font-bold text-xs transform -rotate-45">
                      #{participant.user_id.toString().slice(-4)}
                    </span>
                  </div>
                </div>
              );
            })}

            {/* Center Circle */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-20 h-20 bg-yellow-400 rounded-full border-4 border-white shadow-lg flex items-center justify-center">
              <Sparkles className="w-8 h-8 text-purple-900" />
            </div>
          </div>
        </div>
      </div>

      {/* Spin Button */}
      {!winner && (
        <div className="px-6">
          <button
            onClick={spinWheel}
            disabled={spinning || participants.length === 0}
            className={`w-full py-4 rounded-xl font-bold text-lg transition-all ${
              spinning || participants.length === 0
                ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                : 'bg-gradient-to-r from-yellow-400 to-yellow-500 text-purple-900 hover:shadow-lg hover:scale-105 active:scale-95'
            }`}
          >
            {spinning ? (
              <span className="flex items-center justify-center space-x-2">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-purple-900"></div>
                <span>Крутим барабан...</span>
              </span>
            ) : participants.length === 0 ? (
              'Нет участников'
            ) : (
              '🎲 Запустить розыгрыш!'
            )}
          </button>
        </div>
      )}

      {/* Winner Announcement */}
      {winner && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 animate-fadeIn">
          <div className="bg-gradient-to-br from-yellow-400 to-yellow-500 rounded-3xl p-8 max-w-sm mx-4 text-center shadow-2xl animate-scaleIn">
            <div className="animate-bounce mb-4">
              <Trophy className="w-20 h-20 text-purple-900 mx-auto" />
            </div>
            <h2 className="text-3xl font-bold text-purple-900 mb-2">
              🎉 Победитель! 🎉
            </h2>
            <p className="text-purple-800 mb-4">ID участника:</p>
            <p className="text-5xl font-bold text-purple-900 mb-6">
              #{winner.toString().slice(-6)}
            </p>
            <div className="bg-purple-900 text-yellow-400 rounded-xl p-4 mb-6">
              <p className="text-sm mb-1">Выигрыш</p>
              <p className="text-3xl font-bold">
                {(currentRaffle.entry_price * participants.length * 0.9).toFixed(0)} ₽
              </p>
            </div>
            <button
              onClick={() => navigate(`/raffle/${id}`)}
              className="w-full bg-purple-900 text-yellow-400 py-3 rounded-xl font-bold hover:bg-purple-800 transition-colors"
            >
              Вернуться к розыгрышу
            </button>
          </div>
        </div>
      )}

      {/* Participants List */}
      <div className="px-6 mt-8">
        <h3 className="text-xl font-bold mb-4">Участники ({participants.length})</h3>
        <div className="space-y-2">
          {participants.map((participant, index) => (
            <div
              key={participant.user_id}
              className="bg-white/10 backdrop-blur-sm rounded-lg p-3 flex items-center justify-between border border-white/20"
            >
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center font-bold">
                  {index + 1}
                </div>
                <div>
                  <p className="font-medium">ID: #{participant.user_id.toString().slice(-6)}</p>
                  <p className="text-xs text-purple-200">
                    Билетов: {participant.tickets_count}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="font-bold text-yellow-400">
                  {participant.amount_paid.toFixed(0)} ₽
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.5);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }

        .animate-scaleIn {
          animation: scaleIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
        }
      `}</style>
    </div>
  );
}
